/**
 * LiveTestersAlert — admin-only floating alert showing beta testers who are
 * live in the app RIGHT NOW (heartbeat within the last ~6 minutes), so the
 * owner knows to hold off on code changes / restarts.
 *
 * Polls /api/admin/activity/live every 45s. Dismissible — reappears when the
 * set of live testers changes.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Radio } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export default function LiveTestersAlert() {
  const { user } = useAuth();
  const [testers, setTesters] = useState([]);
  const [dismissedKey, setDismissedKey] = useState("");

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/activity/live");
        if (alive) setTesters(data.testers || []);
      } catch { /* silent — presence is best-effort */ }
    };
    poll();
    const t = setInterval(poll, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdmin]);

  if (!isAdmin || testers.length === 0) return null;
  const key = testers.map((t) => t.email).sort().join(",");
  if (key === dismissedKey) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] rounded-lg p-3.5 shadow-2xl"
      style={{
        background: "#7F1D1D",
        border: "2px solid #FCA5A5",
        maxWidth: 320,
        boxShadow: "0 10px 40px rgba(127,29,29,0.5)",
      }}
      data-testid="live-testers-alert"
    >
      <div className="flex items-start gap-2.5">
        <span className="relative flex h-3 w-3 mt-0.5 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#F87171" }} />
          <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: "#EF4444" }} />
        </span>
        <div className="min-w-0">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "#FCA5A5", fontWeight: 700 }}>
            <Radio size={10} className="inline mr-1" />
            {testers.length} tester{testers.length > 1 ? "s" : ""} live right now
          </div>
          <div className="text-[12px] leading-snug mb-1.5" style={{ color: "#FEE2E2" }}>
            Hold off on code changes or restarts — someone's mid-session.
          </div>
          <div className="font-mono-rm text-[10px] truncate" style={{ color: "#FECACA" }}
               data-testid="live-testers-names">
            {testers.map((t) => t.name || t.email).join(" · ")}
          </div>
          <Link to="/admin/activity"
                className="inline-block mt-1.5 font-mono-rm text-[10px] uppercase tracking-widest underline"
                style={{ color: "#FCD34D", fontWeight: 700 }}
                data-testid="live-testers-link">
            Watch live activity →
          </Link>
        </div>
        <button
          onClick={() => setDismissedKey(key)}
          className="flex-shrink-0 -mt-1 -mr-1 p-1 rounded hover:opacity-70"
          style={{ color: "#FCA5A5" }}
          aria-label="Dismiss"
          data-testid="live-testers-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
